import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import CropOutlinedIcon from "@mui/icons-material/CropOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import {
  addSkillAgentMoodboardAssets,
  autoSkillAgentMoodboardPages,
  contourStrokeSkillAgentMoodboardAsset,
  cropSkillAgentMoodboardAsset,
  deleteSkillAgentMoodboard,
  deleteSkillAgentMoodboardAsset,
  exportSkillAgentMoodboardPdf,
  fetchSkillAgentMoodboardAssetBlobUrl,
  getSkillAgentMoodboardById,
  getSkillAgentMoodboardPool,
  removeBgSkillAgentMoodboardAsset,
  saveSkillAgentMoodboardPages,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import {
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import { pageFieldSx } from "../../styles/pageModernStyles";
import { socket } from "../../socket";
import { moodboardStatusLabel } from "./skillAgentTaskTree";
import {
  applyAviatorPresetToPage,
  clampSlot,
  coverFit,
  emptyFreePage,
  newSlotId,
  normalizeChromeStyle,
  normalizeFreePage,
  normalizePages,
  snapSlot,
  sortSlotsByLayer,
  splitCategoryLabel,
  splitStudioLine,
} from "./skillAgentMoodboardLayout";

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function AssetThumb({
  assetId,
  onClick,
  selected,
  zoom = 1,
  panX = 0.5,
  panY = 0.5,
  fillSlot = false,
  transparentBg = false,
  /** "cover" (default) o "contain" — contain encaja la foto entera en la casilla */
  fit = "cover",
}) {
  const boxRef = useRef(null);
  const [src, setSrc] = useState("");
  const [nat, setNat] = useState(null);
  const [boxPx, setBoxPx] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let revoked = false;
    let url = "";
    setNat(null);
    (async () => {
      try {
        url = await fetchSkillAgentMoodboardAssetBlobUrl(assetId);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId]);

  useEffect(() => {
    if (!fillSlot) return undefined;
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      const r = el.getBoundingClientRect();
      setBoxPx({ w: r.width, h: r.height });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillSlot, src]);

  const fitted =
    fillSlot && nat && boxPx.w > 1 && boxPx.h > 1
      ? coverFit(
          nat.w,
          nat.h,
          { x: 0, y: 0, w: boxPx.w, h: boxPx.h },
          {
            zoom,
            panX,
            panY,
          },
        )
      : null;

  const objectFit = fit === "contain" ? "contain" : "cover";

  return (
    <Box
      ref={boxRef}
      onClick={(e) => onClick?.(e)}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: transparentBg
          ? fit === "contain"
            ? "#f0f0f0"
            : "transparent"
          : fillSlot
            ? "#ebebeb"
            : "action.hover",
        cursor: onClick ? "pointer" : "default",
        outline: selected ? "2px solid" : "none",
        outlineColor: "primary.main",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          draggable={false}
          data-slot-img="1"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img?.naturalWidth && img?.naturalHeight) {
              setNat({ w: img.naturalWidth, h: img.naturalHeight });
            }
          }}
          sx={
            fillSlot && fitted
              ? {
                  position: "absolute",
                  left: fitted.x,
                  top: fitted.y,
                  width: fitted.w,
                  height: fitted.h,
                  objectFit: "fill",
                  display: "block",
                  pointerEvents: "none",
                  userSelect: "none",
                }
              : {
                  width: "100%",
                  height: "100%",
                  objectFit,
                  objectPosition: "center center",
                  display: "block",
                  pointerEvents: "none",
                  opacity: fillSlot ? 0 : 1,
                }
          }
        />
      ) : (
        <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
          <CircularProgress size={14} />
        </Box>
      )}
    </Box>
  );
}

function clamp01Local(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.5;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function normalizeCropUi(rect) {
  let x = clamp01Local(rect.x ?? 0);
  let y = clamp01Local(rect.y ?? 0);
  let w = clamp01Local(rect.w ?? 0.8);
  let h = clamp01Local(rect.h ?? 0.8);
  if (w < 0.05) w = 0.05;
  if (h < 0.05) h = 0.05;
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  return { x, y, w: Math.min(w, 1 - x), h: Math.min(h, 1 - y) };
}

/** Interactive crop dialog — rect in normalized 0–1 of the image. */
function MoodboardCropDialog({ open, assetId, busy, onClose, onConfirm }) {
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const [src, setSrc] = useState("");
  const [rect, setRect] = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });

  useEffect(() => {
    if (!open || !assetId) return undefined;
    let revoked = false;
    let url = "";
    setRect({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    (async () => {
      try {
        url = await fetchSkillAgentMoodboardAssetBlobUrl(assetId);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, assetId]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      const box = boxRef.current;
      if (!d || !box) return;
      const r = box.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      const mx = (e.clientX - r.left) / r.width;
      const my = (e.clientY - r.top) / r.height;
      const dx = mx - d.startMx;
      const dy = my - d.startMy;
      const o = d.orig;
      let next = { ...o };
      if (d.mode === "move") {
        next.x = o.x + dx;
        next.y = o.y + dy;
      } else if (d.mode === "se") {
        next.w = o.w + dx;
        next.h = o.h + dy;
      } else if (d.mode === "nw") {
        next.x = o.x + dx;
        next.y = o.y + dy;
        next.w = o.w - dx;
        next.h = o.h - dy;
      } else if (d.mode === "ne") {
        next.y = o.y + dy;
        next.w = o.w + dx;
        next.h = o.h - dy;
      } else if (d.mode === "sw") {
        next.x = o.x + dx;
        next.w = o.w - dx;
        next.h = o.h + dy;
      }
      setRect(normalizeCropUi(next));
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [open]);

  const startDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    dragRef.current = {
      mode,
      orig: { ...rect },
      startMx: (e.clientX - r.left) / r.width,
      startMy: (e.clientY - r.top) / r.height,
    };
  };

  const handlePts = [
    {
      key: "nw",
      left: `${rect.x * 100}%`,
      top: `${rect.y * 100}%`,
      cursor: "nwse-resize",
    },
    {
      key: "ne",
      left: `${(rect.x + rect.w) * 100}%`,
      top: `${rect.y * 100}%`,
      cursor: "nesw-resize",
    },
    {
      key: "sw",
      left: `${rect.x * 100}%`,
      top: `${(rect.y + rect.h) * 100}%`,
      cursor: "nesw-resize",
    },
    {
      key: "se",
      left: `${(rect.x + rect.w) * 100}%`,
      top: `${(rect.y + rect.h) * 100}%`,
      cursor: "nwse-resize",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={800}>Recortar foto</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Arrastra el marco o las esquinas. Se crea una foto nueva y sustituye
          la de la casilla (zoom vuelve a 100%).
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            bgcolor: "#111",
            borderRadius: 2,
            overflow: "auto",
            maxHeight: "60vh",
            p: 1,
          }}
        >
          <Box
            ref={boxRef}
            sx={{
              position: "relative",
              display: "inline-block",
              maxWidth: "100%",
              userSelect: "none",
              touchAction: "none",
              lineHeight: 0,
            }}
          >
            {src ? (
              <Box
                component="img"
                src={src}
                alt=""
                draggable={false}
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "56vh",
                  width: "auto",
                  height: "auto",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <Box
                sx={{ display: "grid", placeItems: "center", py: 8, px: 10 }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {src ? (
              <>
                <Box
                  onPointerDown={(e) => startDrag("move", e)}
                  sx={{
                    position: "absolute",
                    left: `${rect.x * 100}%`,
                    top: `${rect.y * 100}%`,
                    width: `${rect.w * 100}%`,
                    height: `${rect.h * 100}%`,
                    border: "2px solid #fff",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                    cursor: "move",
                    zIndex: 2,
                  }}
                />
                {handlePts.map((h) => (
                  <Box
                    key={h.key}
                    onPointerDown={(e) => startDrag(h.key, e)}
                    sx={{
                      position: "absolute",
                      left: h.left,
                      top: h.top,
                      width: 14,
                      height: 14,
                      ml: "-7px",
                      mt: "-7px",
                      bgcolor: "#fff",
                      border: "1px solid #333",
                      borderRadius: 0.5,
                      cursor: h.cursor,
                      zIndex: 3,
                    }}
                  />
                ))}
              </>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={busy || !src}
          onClick={() => onConfirm?.(normalizeCropUi(rect))}
        >
          {busy ? "Recortando…" : "Recortar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PoolThumb({
  item,
  checked,
  onToggle,
  height = 148,
  alreadyInBoard = false,
}) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let revoked = false;
    let url = "";
    (async () => {
      try {
        url = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => {
        if (alreadyInBoard) return;
        onToggle?.(!checked);
      }}
      title={
        alreadyInBoard
          ? "Ya está en este moodboard"
          : checked
            ? "Quitar de la selección"
            : "Marcar para añadir"
      }
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: checked
          ? "primary.main"
          : alreadyInBoard
            ? "transparent"
            : "divider",
        cursor: alreadyInBoard ? "default" : "pointer",
        bgcolor: "#eee",
        height,
        minHeight: height,
        maxHeight: height,
        opacity: alreadyInBoard ? 0.45 : 1,
      }}
    >
      {!alreadyInBoard ? (
        <Checkbox
          size="small"
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle?.(e.target.checked)}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.25,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      ) : (
        <Chip
          size="small"
          label="En board"
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 2,
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 700,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      )}
      {src ? (
        <Box
          component="img"
          src={src}
          alt={item.Title || item.BrandName || ""}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          px: 0.75,
          py: 0.4,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.55)",
          lineHeight: 1.25,
        }}
      >
        {item.BrandName || item.Title || `#${item.id}`}
        {item.SearchTitulo ? ` · ${item.SearchTitulo}` : ""}
      </Typography>
    </Box>
  );
}

/** Dialog: marcar fotos de SEARCH del cliente/temporada y añadirlas al board. */
function MoodboardAddPhotosDialog({
  open,
  onClose,
  busy,
  pool,
  poolLoading,
  poolError,
  usedSearchItemIds,
  pick,
  setPick,
  onConfirm,
  seasonLabel,
  clientLabel,
}) {
  const [searchFilter, setSearchFilter] = useState("all");
  const [showCount, setShowCount] = useState(36);

  useEffect(() => {
    if (!open) return;
    setSearchFilter("all");
    setShowCount(36);
  }, [open]);

  const searches = pool?.searches || [];
  const items = useMemo(() => {
    const list = pool?.items || [];
    if (searchFilter === "all") return list;
    return list.filter((it) => Number(it.SearchId) === Number(searchFilter));
  }, [pool?.items, searchFilter]);

  const pickCount = pick?.size || 0;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={800} component="div">
          Añadir fotos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          SEARCH de <strong>{clientLabel || "este cliente"}</strong>
          {seasonLabel ? (
            <>
              {" "}
              · <strong>{seasonLabel}</strong>
            </>
          ) : null}
          . Marca las que quieras; las colocamos después.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {poolError ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {poolError}
          </Alert>
        ) : null}
        {searches.length > 1 ? (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5 }}
          >
            <Chip
              size="small"
              label="Todos"
              color={searchFilter === "all" ? "primary" : "default"}
              variant={searchFilter === "all" ? "filled" : "outlined"}
              onClick={() => setSearchFilter("all")}
              sx={{ fontWeight: 700 }}
            />
            {searches.map((s) => (
              <Chip
                key={s.id}
                size="small"
                label={s.Titulo || `SEARCH #${s.id}`}
                color={
                  Number(searchFilter) === Number(s.id) ? "primary" : "default"
                }
                variant={
                  Number(searchFilter) === Number(s.id) ? "filled" : "outlined"
                }
                onClick={() => setSearchFilter(String(s.id))}
                sx={{ fontWeight: 700, maxWidth: 180 }}
              />
            ))}
          </Stack>
        ) : null}

        {poolLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !items.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              No hay fotos en el SEARCH
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Lanza un SEARCH de este cliente/temporada o cambia el filtro.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 1,
              }}
            >
              {items.slice(0, showCount).map((it) => {
                const already = usedSearchItemIds.has(Number(it.id));
                return (
                  <PoolThumb
                    key={it.id}
                    item={it}
                    height={140}
                    alreadyInBoard={already}
                    checked={!already && pick.has(Number(it.id))}
                    onToggle={(on) => {
                      setPick((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(Number(it.id));
                        else next.delete(Number(it.id));
                        return next;
                      });
                    }}
                  />
                );
              })}
            </Box>
            {items.length > showCount ? (
              <Button
                size="small"
                sx={{ mt: 1.5, textTransform: "none" }}
                onClick={() => setShowCount((n) => n + 36)}
              >
                Ver más ({items.length - showCount})
              </Button>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={busy || pickCount === 0}
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {busy
            ? "Añadiendo…"
            : pickCount
              ? `Añadir ${pickCount} foto${pickCount === 1 ? "" : "s"}`
              : "Añadir"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Dialog: fotos ya en el board — marcar, colocar en página, quitar. */
function MoodboardBoardPhotosDialog({
  open,
  onClose,
  assets,
  selectedIds,
  usedOnPagesIds,
  onToggleSelect,
  onClearSelect,
  onPlaceOne,
  onPlaceSelected,
  onRemove,
  pageLabel,
  busy,
}) {
  const selected = selectedIds instanceof Set ? selectedIds : new Set();
  const usedOnPages =
    usedOnPagesIds instanceof Set ? usedOnPagesIds : new Set();
  const nSel = selected.size;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Box>
            <Typography variant="h6" fontWeight={800} component="div">
              Fotos del board ({assets.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Clic = colocar en la casilla / hueco · marca varias y colócalas en{" "}
              {pageLabel}. Las que ya están en páginas se ven atenuadas.
            </Typography>
          </Box>
          {nSel > 0 ? (
            <Chip
              size="small"
              color="primary"
              label={`${nSel} marcada${nSel === 1 ? "" : "s"}`}
              onDelete={onClearSelect}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {!assets.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              Aún no hay fotos en el board
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Usa «Añadir fotos» o súbelas del PC.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 1.25,
            }}
          >
            {assets.map((a) => {
              const id = Number(a.id);
              const isSelected = selected.has(id);
              const alreadyOnPage = usedOnPages.has(id);
              const isCutout =
                String(a.Kind || "").toLowerCase() === "cutout" ||
                /\(\s*sin fondo\s*\)/i.test(String(a.Title || ""));
              return (
                <Box
                  key={a.id}
                  title={
                    alreadyOnPage
                      ? "Ya está en una página · clic para colocar otra vez"
                      : undefined
                  }
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: "#f3f3f3",
                    aspectRatio: "1 / 1.15",
                    opacity: alreadyOnPage ? 0.45 : 1,
                    boxShadow: isSelected
                      ? `0 0 0 1px ${alpha("#0ea5e9", 0.35)}`
                      : "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      cursor: "pointer",
                    }}
                    onClick={() => onPlaceOne?.(a.id)}
                  >
                    <AssetThumb
                      assetId={a.id}
                      selected={isSelected}
                      transparentBg
                      fit="contain"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={
                      isSelected ? "Quitar de la selección" : "Marcar foto"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelect?.(a.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      bgcolor: alpha("#fff", 0.95),
                      boxShadow: 1,
                      color: isSelected ? "primary.main" : "text.secondary",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    {isSelected ? (
                      <CheckBoxOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Eliminar foto"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.(a.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      bgcolor: alpha("#fff", 0.95),
                      boxShadow: 1,
                      color: "error.main",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  {isCutout ? (
                    <Chip
                      size="small"
                      label="Sin fondo"
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        zIndex: 2,
                        height: 22,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        bgcolor: alpha("#fff", 0.92),
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          disabled={!nSel}
          startIcon={<DashboardCustomizeOutlinedIcon />}
          onClick={onPlaceSelected}
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          {nSel ? `Colocar ${nSel} en ${pageLabel}` : `Colocar en ${pageLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FreeformSlotBox({
  slot,
  selected,
  onSelect,
  onChange,
  onRequestRemove,
  canRemove,
  canvasRef,
}) {
  const dragRef = useRef(null);
  const rootRef = useRef(null);
  const slotRef = useRef(slot);
  const selectedRef = useRef(selected);
  slotRef.current = slot;
  selectedRef.current = selected;

  const emitSlot = (partial, { snap = true } = {}) => {
    const next = clampSlot({
      ...slot,
      ...partial,
      id: slot.id,
      assetId: slot.assetId,
    });
    onChange?.(snap ? snapSlot(next) : next);
  };

  /* Alt+rueda: passive:false para que preventDefault pare el scroll de página */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onWheelNative = (e) => {
      const s = slotRef.current;
      if (!selectedRef.current || !s?.assetId || !e.altKey) return;
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.12 : 0.12;
      const zoom = Math.min(8, Math.max(0.25, (Number(s.zoom) || 1) + delta));
      onChange?.(
        clampSlot({
          ...s,
          id: s.id,
          assetId: s.assetId,
          zoom,
        }),
      );
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, [onChange]);

  const startGesture = (mode, handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const slotRect = e.currentTarget.getBoundingClientRect();
    const imgEl = e.currentTarget.querySelector("img[data-slot-img]");
    const iw = Math.max(1, imgEl?.naturalWidth || 1);
    const ih = Math.max(1, imgEl?.naturalHeight || 1);
    dragRef.current = {
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...slot },
      cw: rect.width,
      ch: rect.height,
      sw: Math.max(1, slotRect.width),
      sh: Math.max(1, slotRect.height),
      iw,
      ih,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      let next = { ...d.orig };
      if (d.mode === "pan") {
        // Mover en píxeles reales de la casilla (misma caja que el render).
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const box = { x: 0, y: 0, w: d.sw, h: d.sh };
        const fitted0 = coverFit(d.iw, d.ih, box, {
          zoom: d.orig.zoom,
          panX: d.orig.panX,
          panY: d.orig.panY,
        });
        const newX = fitted0.x + dx;
        const newY = fitted0.y + dy;
        const denomW = box.w - fitted0.w;
        const denomH = box.h - fitted0.h;
        next.panX =
          Math.abs(denomW) < 0.0001
            ? 0.5
            : clamp01Local((newX - box.x) / denomW);
        next.panY =
          Math.abs(denomH) < 0.0001
            ? 0.5
            : clamp01Local((newY - box.y) / denomH);
        onChange?.(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        );
        return;
      }
      if (d.mode === "move") {
        const dx = (ev.clientX - d.startX) / d.cw;
        const dy = (ev.clientY - d.startY) / d.ch;
        next.x = d.orig.x + dx;
        next.y = d.orig.y + dy;
      } else {
        const dx = (ev.clientX - d.startX) / d.cw;
        const dy = (ev.clientY - d.startY) / d.ch;
        const h = d.handle;
        if (h.includes("e")) next.w = d.orig.w + dx;
        if (h.includes("s")) next.h = d.orig.h + dy;
        if (h.includes("w")) {
          next.x = d.orig.x + dx;
          next.w = d.orig.w - dx;
        }
        if (h.includes("n")) {
          next.y = d.orig.y + dy;
          next.h = d.orig.h - dy;
        }
      }
      onChange?.(
        snapSlot(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        ),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const bumpZoom = (dir) => {
    const zoom = Math.min(
      8,
      Math.max(0.25, (Number(slot.zoom) || 1) + dir * 0.25),
    );
    emitSlot({ zoom }, { snap: false });
  };

  const pinchDist = (t0, t1) => {
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e) => {
    if (!slot.assetId || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id);
    dragRef.current = {
      mode: "pinch",
      orig: { ...slot },
      startDist: pinchDist(e.touches[0], e.touches[1]),
    };
  };

  const onTouchMove = (e) => {
    const d = dragRef.current;
    if (!d || d.mode !== "pinch" || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    const dist = pinchDist(e.touches[0], e.touches[1]);
    if (!d.startDist || d.startDist < 1) return;
    const ratio = dist / d.startDist;
    const zoom = Math.min(
      8,
      Math.max(0.25, (Number(d.orig.zoom) || 1) * ratio),
    );
    onChange?.(
      clampSlot({
        ...d.orig,
        id: slot.id,
        assetId: slot.assetId,
        zoom,
      }),
    );
  };

  const onTouchEnd = () => {
    if (dragRef.current?.mode === "pinch") dragRef.current = null;
  };

  const isCircle = String(slot.shape || "rect") === "circle";
  const hasAsset = Boolean(slot.assetId);

  return (
    <Box
      ref={rootRef}
      onPointerDown={(e) => {
        // Ignore second pointer during pinch (touch handles it).
        if (e.pointerType === "touch" && dragRef.current?.mode === "pinch") {
          return;
        }
        // Como Illustrator/InDesign: arrastrar = marco; Alt = foto dentro.
        if (slot.assetId && e.altKey) {
          startGesture("pan", null, e);
        } else {
          startGesture("move", null, e);
        }
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(slot.id);
      }}
      sx={{
        position: "absolute",
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        width: `${slot.w * 100}%`,
        height: `${slot.h * 100}%`,
        bgcolor: hasAsset ? "transparent" : "transparent",
        overflow: "hidden",
        borderRadius: isCircle ? "50%" : 0,
        outline: selected
          ? "2px solid"
          : hasAsset
            ? "1px solid transparent"
            : "1px dashed rgba(0,0,0,0.28)",
        outlineColor: selected ? "primary.main" : undefined,
        outlineOffset: -1,
        cursor: "move",
        zIndex: selected
          ? 1000 + (Number(slot.z) || 0)
          : 1 + (Number(slot.z) || 0),
        touchAction: "none",
        userSelect: "none",
        "&:active": {
          cursor: selected && slot.assetId ? "grabbing" : "move",
        },
        "&:hover .slot-remove, &:hover .slot-zoom": { opacity: 1 },
      }}
    >
      {slot.assetId ? (
        <AssetThumb
          assetId={slot.assetId}
          fillSlot
          transparentBg
          zoom={slot.zoom}
          panX={slot.panX}
          panY={slot.panY}
        />
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "grid",
            placeItems: "center",
            height: "100%",
            fontSize: "0.65rem",
            pointerEvents: "none",
          }}
        >
          {isCircle ? "○ vacía" : "vacía"}
        </Typography>
      )}
      {selected && slot.assetId ? (
        <Stack
          className="slot-zoom"
          direction="row"
          spacing={0.25}
          sx={{
            position: "absolute",
            left: 4,
            bottom: 4,
            zIndex: 7,
            opacity: 1,
            bgcolor: alpha("#0f172a", 0.78),
            borderRadius: 1.5,
            p: 0.35,
          }}
        >
          <Tooltip title="Alejar">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                bumpZoom(-1);
              }}
              disabled={(slot.zoom || 1) <= 0.25}
              sx={{ color: "#fff", p: 0.75, minWidth: 36, minHeight: 36 }}
            >
              <ZoomOutOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              fontWeight: 700,
              px: 0.5,
              alignSelf: "center",
              fontSize: "0.75rem",
              minWidth: 36,
              textAlign: "center",
            }}
          >
            {Math.round((slot.zoom || 1) * 100)}%
          </Typography>
          <Tooltip title="Acercar">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                bumpZoom(1);
              }}
              disabled={(slot.zoom || 1) >= 8}
              sx={{ color: "#fff", p: 0.75, minWidth: 36, minHeight: 36 }}
            >
              <ZoomInOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
      {canRemove ? (
        <Tooltip title="Quitar casilla">
          <IconButton
            className="slot-remove"
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRequestRemove?.(slot.id);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 7,
              opacity: selected ? 1 : 0,
              transition: "opacity 0.12s",
              bgcolor: alpha("#fff", 0.92),
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              p: 0.35,
              "&:hover": { bgcolor: "#fff", color: "error.main" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {selected
        ? HANDLES.map((h) => (
            <Box
              key={h}
              onPointerDown={(e) => startGesture("resize", h, e)}
              sx={{
                position: "absolute",
                width: 10,
                height: 10,
                bgcolor: "primary.main",
                border: "1px solid #fff",
                borderRadius: 0.25,
                zIndex: 6,
                ...(h.includes("n") ? { top: -5 } : null),
                ...(h.includes("s") ? { bottom: -5 } : null),
                ...(h.includes("w") ? { left: -5 } : null),
                ...(h.includes("e") ? { right: -5 } : null),
                ...(h === "n" || h === "s"
                  ? { left: "50%", marginLeft: "-5px" }
                  : null),
                ...(h === "e" || h === "w"
                  ? { top: "50%", marginTop: "-5px" }
                  : null),
                cursor: `${h}-resize`,
              }}
            />
          ))
        : null}
    </Box>
  );
}

async function loadPoolFromSearches(board) {
  const customerId = board?.CustomerId ? Number(board.CustomerId) : null;
  const temporada = String(board?.Temporada || "").trim();
  const list = await listSkillAgentSearches({
    ...(customerId ? { customerId } : {}),
    ...(temporada ? { temporada } : {}),
  });
  const searches = (Array.isArray(list) ? list : []).filter((s) => {
    if (customerId && Number(s.CustomerId) !== customerId) return false;
    if (temporada && String(s.Temporada || "").trim() !== temporada) {
      return false;
    }
    return true;
  });
  const items = [];
  for (const s of searches) {
    const detail = await getSkillAgentSearchById(s.id);
    const imgs = (Array.isArray(detail?.items) ? detail.items : []).filter(
      (it) => !it.Kind || String(it.Kind).toLowerCase() === "image",
    );
    for (const it of imgs) {
      items.push({
        id: it.id,
        SearchId: s.id,
        Title: it.Title,
        BrandName: it.BrandName,
        BrandId: it.BrandId,
        SourceUrl: it.SourceUrl,
        MimeType: it.MimeType,
        DriveFileUrl: it.DriveFileUrl,
        SearchTitulo: s.Titulo || null,
      });
    }
  }
  return {
    searches: searches.map((s) => ({
      id: s.id,
      Titulo: s.Titulo,
      Status: s.Status,
      Temporada: s.Temporada,
      CustomerId: s.CustomerId,
    })),
    items,
  };
}

export default function SkillAgentMoodboardDetail({
  moodboardId,
  onChanged,
  onDeleted,
  onOpenHelp,
  viewerName = "",
  viewerUserId = null,
}) {
  const canvasRef = useRef(null);
  const skipAutosaveRef = useRef(true);
  const pagesRef = useRef([emptyFreePage()]);
  const metaRef = useRef({
    titulo: "",
    categoryLabel: "",
    studioLine: "",
    chromeStyle: normalizeChromeStyle(null),
  });
  const expectedUpdatedAtRef = useRef(null);
  const saveStateRef = useRef("saved");
  const saveChainRef = useRef(Promise.resolve());
  const pagesTimerRef = useRef(null);
  const metaTimerRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  const softRefreshBusyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | unsaved | error
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [peerViewers, setPeerViewers] = useState([]);
  const [staleConflict, setStaleConflict] = useState(false);
  const [board, setBoard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pages, setPages] = useState([emptyFreePage()]);
  const [pageIdx, setPageIdx] = useState(0);
  const [activeSlotId, setActiveSlotId] = useState(null);
  const [pool, setPool] = useState({ searches: [], items: [] });
  const [poolPick, setPoolPick] = useState(() => new Set());
  const [poolError, setPoolError] = useState("");
  const [poolLoading, setPoolLoading] = useState(false);
  const [addPhotosOpen, setAddPhotosOpen] = useState(false);
  const [boardPhotosOpen, setBoardPhotosOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [slotRemoveId, setSlotRemoveId] = useState(null);
  const [assetRemoveId, setAssetRemoveId] = useState(null);
  const [pageRemoveOpen, setPageRemoveOpen] = useState(false);
  const [aviatorConfirmOpen, setAviatorConfirmOpen] = useState(false);
  const [autoPagesConfirmOpen, setAutoPagesConfirmOpen] = useState(false);
  const [removeBgConfirmOpen, setRemoveBgConfirmOpen] = useState(false);
  const [gallerySelected, setGallerySelected] = useState(() => new Set());
  const [titulo, setTitulo] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [studioLine, setStudioLine] = useState("");
  const [chromeStyle, setChromeStyle] = useState(() =>
    normalizeChromeStyle(null),
  );
  const [metaOpen, setMetaOpen] = useState(false);
  const [layoutMenuEl, setLayoutMenuEl] = useState(null);
  const [strokeAnchor, setStrokeAnchor] = useState(null);
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [cropOpen, setCropOpen] = useState(false);

  const load = useCallback(async () => {
    if (!moodboardId) return;
    skipAutosaveRef.current = true;
    setLoading(true);
    setError("");
    setPoolError("");
    setSaveState("saved");
    try {
      const data = await getSkillAgentMoodboardById(moodboardId);
      setBoard(data);
      if (data?.updatedAt) expectedUpdatedAtRef.current = data.updatedAt;
      setStaleConflict(false);
      setPeerViewers([]);
      setAssets(Array.isArray(data?.assets) ? data.assets : []);
      setPages(normalizePages(data?.PagesJson));
      setTitulo(data?.Titulo || "");
      setCategoryLabel(data?.CategoryLabel || "");
      setStudioLine(data?.StudioLine || "");
      setChromeStyle(normalizeChromeStyle(data?.ChromeStyleJson));
      setActiveSlotId(null);

      setPoolLoading(true);
      let poolData = { searches: [], items: [] };
      try {
        poolData = await getSkillAgentMoodboardPool(moodboardId);
      } catch (poolErr) {
        try {
          poolData = await loadPoolFromSearches(data);
        } catch {
          setPoolError(
            poolErr?.response?.data?.error ||
              "No se pudo cargar el pool de SEARCH.",
          );
          poolData = { searches: [], items: [] };
        }
      }
      if (
        !Array.isArray(poolData?.items) ||
        (!poolData.items.length && data?.CustomerId)
      ) {
        try {
          const fallback = await loadPoolFromSearches(data);
          if (fallback.items?.length) poolData = fallback;
        } catch {
          /* keep primary */
        }
      }
      setPool({
        searches: Array.isArray(poolData?.searches) ? poolData.searches : [],
        items: Array.isArray(poolData?.items) ? poolData.items : [],
      });
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar el moodboard.");
    } finally {
      setPoolLoading(false);
      setLoading(false);
      // Enable autosave after React applies the loaded state.
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 100);
    }
  }, [moodboardId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const usedSearchItemIds = useMemo(() => {
    const s = new Set();
    for (const a of assets) {
      if (a.SearchItemId) s.add(Number(a.SearchItemId));
    }
    return s;
  }, [assets]);

  const usedOnPagesAssetIds = useMemo(() => {
    const s = new Set();
    for (const p of pages || []) {
      for (const slot of p?.slots || []) {
        if (slot?.assetId != null) s.add(Number(slot.assetId));
      }
    }
    return s;
  }, [pages]);

  const currentPage = useMemo(
    () => normalizeFreePage(pages[pageIdx] || emptyFreePage()),
    [pages, pageIdx],
  );
  const activeSlot = useMemo(
    () => (currentPage.slots || []).find((s) => s.id === activeSlotId) || null,
    [currentPage, activeSlotId],
  );
  const activeAsset = useMemo(() => {
    if (!activeSlot?.assetId) return null;
    return (
      assets.find((a) => Number(a.id) === Number(activeSlot.assetId)) || null
    );
  }, [assets, activeSlot]);
  const activeAssetIsCutout = useMemo(() => {
    if (!activeAsset) return false;
    const kind = String(activeAsset.Kind || "").toLowerCase();
    if (kind === "cutout") return true;
    return /\(\s*sin fondo\s*\)/i.test(String(activeAsset.Title || ""));
  }, [activeAsset]);
  const layeredSlots = useMemo(
    () => sortSlotsByLayer(currentPage.slots || []),
    [currentPage],
  );

  const applyBoard = (data, { fromServer = true } = {}) => {
    if (!data) return;
    if (fromServer) skipAutosaveRef.current = true;
    setBoard(data);
    if (data.updatedAt) expectedUpdatedAtRef.current = data.updatedAt;
    if (Array.isArray(data.assets)) setAssets(data.assets);
    if (data.PagesJson) setPages(normalizePages(data.PagesJson));
    if (data.Titulo != null) setTitulo(data.Titulo);
    if (data.CategoryLabel != null) setCategoryLabel(data.CategoryLabel);
    if (data.StudioLine != null) setStudioLine(data.StudioLine);
    if (data.ChromeStyleJson != null) {
      setChromeStyle(normalizeChromeStyle(data.ChromeStyleJson));
    }
    if (fromServer) {
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 80);
    }
  };

  const updateCurrentPageSlots = (updater) => {
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        const slots =
          typeof updater === "function" ? updater(base.slots) : updater;
        return { slots: slots.map((s) => clampSlot(s)) };
      }),
    );
  };

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    metaRef.current = { titulo, categoryLabel, studioLine, chromeStyle };
  }, [titulo, categoryLabel, studioLine, chromeStyle]);

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);

  const handleStaleConflict = useCallback(async () => {
    setStaleConflict(true);
    setSaveState("error");
    setError(
      "Otro usuario ha guardado este moodboard. Recarga para no pisar sus cambios.",
    );
    try {
      const data = await getSkillAgentMoodboardById(moodboardId);
      applyBoard(data);
      setSaveState("saved");
      setStaleConflict(false);
      setError("");
      setOkMsg("Moodboard recargado (había una versión más nueva).");
    } catch {
      /* keep error */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId]);

  const persistPages = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const run = async () => {
      const payload = pagesRef.current.map((p) => normalizeFreePage(p));
      setSaveState("saving");
      try {
        const data = await saveSkillAgentMoodboardPages(moodboardId, payload, {
          expectedUpdatedAt: expectedUpdatedAtRef.current,
        });
        applyBoard(data);
        setStaleConflict(false);
        setSaveState("saved");
        return true;
      } catch (err) {
        if (err?.response?.status === 409) {
          await handleStaleConflict();
          return false;
        }
        setSaveState("error");
        setError(err?.response?.data?.error || "No se pudo guardar el layout.");
        return false;
      }
    };
    const p = saveChainRef.current.then(run, run);
    saveChainRef.current = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyBoard es local estable en práctica
  }, [moodboardId, exporting, handleStaleConflict]);

  const persistMeta = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const run = async () => {
      const m = metaRef.current;
      setSaveState("saving");
      try {
        const data = await updateSkillAgentMoodboard(moodboardId, {
          Titulo: m.titulo,
          CategoryLabel: m.categoryLabel,
          StudioLine: m.studioLine,
          ChromeStyleJson: m.chromeStyle,
          ExpectedUpdatedAt: expectedUpdatedAtRef.current,
        });
        applyBoard(data);
        setStaleConflict(false);
        setSaveState("saved");
        return true;
      } catch (err) {
        if (err?.response?.status === 409) {
          await handleStaleConflict();
          return false;
        }
        setSaveState("error");
        setError(err?.response?.data?.error || "No se pudo guardar los datos.");
        return false;
      }
    };
    const p = saveChainRef.current.then(run, run);
    saveChainRef.current = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, exporting, handleStaleConflict]);

  const flushAutosave = useCallback(async () => {
    if (pagesTimerRef.current) {
      clearTimeout(pagesTimerRef.current);
      pagesTimerRef.current = null;
    }
    if (metaTimerRef.current) {
      clearTimeout(metaTimerRef.current);
      metaTimerRef.current = null;
    }
    const okPages = await persistPages();
    const okMeta = await persistMeta();
    return okPages && okMeta;
  }, [persistPages, persistMeta]);

  // Presencia + soft refresh si otro guarda
  useEffect(() => {
    if (!moodboardId) return undefined;
    const id = Number(moodboardId);
    if (!Number.isFinite(id) || id <= 0) return undefined;
    socket.emit("join-moodboard", {
      moodboardId: id,
      userId: viewerUserId,
      username: viewerName || "Alguien",
    });
    const onPresence = (payload) => {
      if (Number(payload?.moodboardId) !== id) return;
      const viewers = Array.isArray(payload?.viewers) ? payload.viewers : [];
      setPeerViewers(
        viewers.filter((v) => {
          if (viewerUserId == null) return true;
          return Number(v.userId) !== Number(viewerUserId);
        }),
      );
    };
    const onUpdated = async (payload) => {
      if (Number(payload?.moodboardId) !== id) return;
      if (softRefreshBusyRef.current) return;
      if (
        saveStateRef.current === "unsaved" ||
        saveStateRef.current === "saving"
      ) {
        setStaleConflict(true);
        setError(
          "Otro usuario ha guardado este moodboard. Recarga antes de seguir editando.",
        );
        return;
      }
      softRefreshBusyRef.current = true;
      try {
        const data = await getSkillAgentMoodboardById(moodboardId);
        applyBoard(data);
        setStaleConflict(false);
      } catch {
        /* ignore */
      } finally {
        softRefreshBusyRef.current = false;
      }
    };
    socket.on("moodboard-presence", onPresence);
    socket.on("moodboard-updated", onUpdated);
    return () => {
      socket.emit("leave-moodboard", { moodboardId: id });
      socket.off("moodboard-presence", onPresence);
      socket.off("moodboard-updated", onUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, viewerName, viewerUserId]);

  // Autosave layout (debounce)
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    pagesTimerRef.current = setTimeout(() => {
      persistPages();
    }, 750);
    return () => {
      if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    };
  }, [pages, loading, exporting, persistPages]);

  // Autosave título / barras PDF
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    metaTimerRef.current = setTimeout(() => {
      persistMeta();
    }, 900);
    return () => {
      if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    };
  }, [
    titulo,
    categoryLabel,
    studioLine,
    chromeStyle,
    loading,
    exporting,
    persistMeta,
  ]);

  // Aviso si cierran la pestaña con cambios pendientes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (saveState === "unsaved" || saveState === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveState]);

  const handleSaveAll = async () => {
    setBusy(true);
    setError("");
    try {
      const ok = await flushAutosave();
      if (ok) {
        onChangedRef.current?.();
        setOkMsg("Guardado.");
      }
    } finally {
      setBusy(false);
    }
  };

  const assignAssetToSlot = (slotId, assetId) => {
    updateCurrentPageSlots((slots) =>
      slots.map((s) =>
        s.id === slotId ? { ...s, assetId: Number(assetId) } : s,
      ),
    );
  };

  const placeAssetInFirstEmpty = (assetId) => {
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      for (let i = 0; i < next.length; i += 1) {
        const empty = next[i].slots.find((s) => s.assetId == null);
        if (empty) {
          empty.assetId = Number(assetId);
          setPageIdx(i);
          setActiveSlotId(empty.id);
          return next;
        }
      }
      const page = emptyFreePage();
      page.slots[0].assetId = Number(assetId);
      setPageIdx(next.length);
      setActiveSlotId(page.slots[0].id);
      return [...next, page];
    });
  };

  const toggleGallerySelect = (assetId) => {
    const id = Number(assetId);
    if (!Number.isFinite(id)) return;
    setGallerySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Coloca la selección en la página actual (huecos primero; si faltan, nuevas casillas). */
  const placeSelectedOnCurrentPage = () => {
    const ids = [...gallerySelected];
    if (!ids.length) return;
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      const page = next[pageIdx] || emptyFreePage();
      if (!next[pageIdx]) next.push(page);
      let slots = [...(page.slots || [])];
      let maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      const queue = [...ids];
      slots = slots.map((s) => {
        if (s.assetId != null || !queue.length) return s;
        return clampSlot({ ...s, assetId: Number(queue.shift()) });
      });
      while (queue.length) {
        maxZ += 1;
        const n = slots.length;
        const col = n % 3;
        const row = Math.floor(n / 3);
        slots.push(
          clampSlot({
            id: newSlotId("p"),
            x: 0.05 + col * 0.32,
            y: 0.08 + row * 0.3,
            w: 0.28,
            h: 0.26,
            shape: "rect",
            z: maxZ,
            assetId: Number(queue.shift()),
          }),
        );
      }
      next[pageIdx] = { slots };
      return next;
    });
    setActiveSlotId(null);
    const n = gallerySelected.size;
    setGallerySelected(new Set());
    setOkMsg(
      `${n} foto${n === 1 ? "" : "s"} colocada${n === 1 ? "" : "s"} en página ${pageIdx + 1}.`,
    );
  };

  const handleAddSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    const slot = clampSlot({
      id: newSlotId(),
      x: 0.35,
      y: 0.35,
      w: 0.28,
      h: 0.3,
      shape: "rect",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    setActiveSlotId(slot.id);
  };

  const handleAddCircleSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    // Landscape page ~297/210 → keep visual circle-ish defaults.
    const slot = clampSlot({
      id: newSlotId("c"),
      x: 0.38,
      y: 0.32,
      w: 0.2,
      h: 0.28,
      shape: "circle",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    setActiveSlotId(slot.id);
  };

  const handleToggleSlotShape = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) =>
      slots.map((s) => {
        if (s.id !== activeSlotId) return s;
        const nextShape = s.shape === "circle" ? "rect" : "circle";
        return clampSlot({ ...s, shape: nextShape });
      }),
    );
  };

  const handleBringToFront = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) => {
      const maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      return slots.map((s) =>
        s.id === activeSlotId ? clampSlot({ ...s, z: maxZ + 1 }) : s,
      );
    });
  };

  const handleSendToBack = () => {
    if (!activeSlotId) return;
    updateCurrentPageSlots((slots) => {
      const minZ = Math.min(...slots.map((s) => Number(s.z) || 0));
      return slots.map((s) =>
        s.id === activeSlotId
          ? clampSlot({ ...s, z: Math.max(0, minZ - 1) })
          : s,
      );
    });
  };

  const applyDerivedAssetResponse = (data, fromAssetId) => {
    if (Array.isArray(data?.assets)) setAssets(data.assets);
    if (Array.isArray(data?.PagesJson)) {
      setPages(normalizePages(data.PagesJson));
    } else if (data?.asset?.id) {
      const nextId = Number(data.asset.id);
      const fromId = Number(fromAssetId);
      setPages((prev) =>
        prev.map((p) => ({
          ...normalizeFreePage(p),
          slots: normalizeFreePage(p).slots.map((s) =>
            Number(s.assetId) === fromId
              ? clampSlot({
                  ...s,
                  assetId: nextId,
                  zoom: 1,
                  panX: 0.5,
                  panY: 0.5,
                })
              : s,
          ),
        })),
      );
    }
    if (data?.Titulo != null || data?.id) {
      setBoard((p) => ({ ...p, ...data, assets: undefined }));
    }
  };

  const runRemoveBackground = async () => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    setRemoveBgConfirmOpen(false);
    try {
      const data = await removeBgSkillAgentMoodboardAsset(moodboardId, assetId);
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      if (Array.isArray(data?.PagesJson)) {
        setPages(normalizePages(data.PagesJson));
      } else if (data?.asset?.id) {
        const nextId = Number(data.asset.id);
        const fromId = Number(assetId);
        setPages((prev) =>
          prev.map((p) => ({
            ...normalizeFreePage(p),
            slots: normalizeFreePage(p).slots.map((s) =>
              Number(s.assetId) === fromId
                ? clampSlot({ ...s, assetId: nextId })
                : s,
            ),
          })),
        );
      }
      if (data?.Titulo != null || data?.id) {
        setBoard((p) => ({ ...p, ...data, assets: undefined }));
      }
      setOkMsg("Fondo quitado · PNG transparente en la casilla");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          "No se pudo quitar el fondo",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveBackground = () => {
    if (!activeSlot?.assetId || busy) return;
    if (activeAssetIsCutout) {
      setRemoveBgConfirmOpen(true);
      return;
    }
    runRemoveBackground();
  };

  const handleApplyContourStroke = async () => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    setStrokeAnchor(null);
    try {
      const data = await contourStrokeSkillAgentMoodboardAsset(
        moodboardId,
        assetId,
        { width: strokeWidth, color: strokeColor },
      );
      applyDerivedAssetResponse(data, assetId);
      setOkMsg(`Borde ${strokeWidth}px aplicado al contorno`);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          "No se pudo aplicar el borde",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCrop = async (rect) => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const data = await cropSkillAgentMoodboardAsset(
        moodboardId,
        assetId,
        rect,
      );
      applyDerivedAssetResponse(data, assetId);
      setCropOpen(false);
      setOkMsg("Foto recortada · sustituida en la casilla");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          "No se pudo recortar",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveSlot = (slotId) => {
    const id = slotId || activeSlotId;
    if (!id) return;
    updateCurrentPageSlots((slots) => {
      if (slots.length <= 1) return slots;
      return slots.filter((s) => s.id !== id);
    });
    setActiveSlotId(null);
    setSlotRemoveId(null);
  };

  const handleConfirmRemovePage = () => {
    if (pages.length <= 1) {
      setPageRemoveOpen(false);
      return;
    }
    const removeAt = pageIdx;
    setPages((prev) =>
      prev.filter((_, i) => i !== removeAt).map(normalizeFreePage),
    );
    setPageIdx((idx) => {
      if (idx > removeAt) return idx - 1;
      if (idx === removeAt) return Math.max(0, Math.min(idx, pages.length - 2));
      return idx;
    });
    setActiveSlotId(null);
    setPageRemoveOpen(false);
    setOkMsg(`Página ${removeAt + 1} eliminada`);
  };

  const handleAviatorPreset = () => {
    setPages((prev) =>
      prev.map((p, i) =>
        i === pageIdx ? applyAviatorPresetToPage(p) : normalizeFreePage(p),
      ),
    );
    setActiveSlotId(null);
    setAviatorConfirmOpen(false);
    setOkMsg(`Página ${pageIdx + 1} recolocada con plantilla Aviator.`);
  };

  const handleAutoPages = async () => {
    setBusy(true);
    try {
      const data = await autoSkillAgentMoodboardPages(moodboardId);
      applyBoard(data);
      setPageIdx(0);
      setActiveSlotId(null);
      setAutoPagesConfirmOpen(false);
      onChanged?.();
      setOkMsg("Páginas repartidas (Aviator 7 / página).");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo repartir.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddFromPool = async (idsOverride) => {
    const rawIds = Array.isArray(idsOverride) ? idsOverride : [...poolPick];
    const ids = rawIds
      .map((id) => Number(id))
      .filter((id) => id && !usedSearchItemIds.has(id));
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, {
        searchItemIds: ids,
      });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      // Solo sincronizamos layout si el servidor rellenó páginas vacías;
      // si no, las fotos quedan en el board sin colocar (siguiente paso).
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (boardWasEmpty && placedOnServer) {
        skipAutosaveRef.current = true;
        setPages(serverPages);
        setTimeout(() => {
          skipAutosaveRef.current = false;
        }, 80);
      }
      setPoolPick(new Set());
      setAddPhotosOpen(false);
      onChanged?.();
      setOkMsg(
        `${added.length || ids.length} foto(s) en el moodboard. Colócalas cuando quieras.`,
      );
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudieron añadir.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, { files });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (boardWasEmpty && placedOnServer) {
        skipAutosaveRef.current = true;
        setPages(serverPages);
        setTimeout(() => {
          skipAutosaveRef.current = false;
        }, 80);
      }
      onChanged?.();
      setOkMsg(`${files.length} subida(s). Colócalas desde la bandeja.`);
    } catch (err) {
      setError(err?.response?.data?.error || "Error al subir.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAsset = async (assetId) => {
    if (!assetId) return;
    setBusy(true);
    try {
      const data = await deleteSkillAgentMoodboardAsset(moodboardId, assetId);
      applyBoard(data);
      setAssetRemoveId(null);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo quitar.");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (exporting || busy) return;
    setExporting(true);
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      await flushAutosave();
      const data = await exportSkillAgentMoodboardPdf(moodboardId, {
        customerFolderName: board?.CustomerName || undefined,
      });
      applyBoard(data.moodboard || data.search || data);

      if (data.pdfBase64) {
        try {
          const bin = atob(data.pdfBase64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = data.pdfFileName || "moodboard.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch {
          /* download optional */
        }
      }

      onChanged?.();
      if (data.drivePdfFileUrl) {
        setOkMsg("PDF exportado y subido a Drive (también descargado).");
      } else if (data.driveNote) {
        setOkMsg(`PDF descargado. Drive: ${data.driveNote}`);
      } else {
        setOkMsg("PDF generado y descargado.");
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Error al exportar PDF.");
    } finally {
      setExporting(false);
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteSkillAgentMoodboard(moodboardId);
      setDeleteOpen(false);
      onDeleted?.(moodboardId);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo eliminar.");
      setBusy(false);
    }
  };

  const catParts = splitCategoryLabel(categoryLabel);
  const studioParts = splitStudioLine(studioLine);

  if (loading && !board) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!board) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Moodboard no encontrado.
      </Alert>
    );
  }

  const startChromeBarDrag = (which, e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveSlotId(null);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const elRect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - elRect.left;
    const offsetY = e.clientY - elRect.top;
    const onMove = (ev) => {
      if (rect.width < 1 || rect.height < 1) return;
      const x = Math.min(
        0.95,
        Math.max(0, (ev.clientX - offsetX - rect.left) / rect.width),
      );
      const y = Math.min(
        0.95,
        Math.max(0, (ev.clientY - offsetY - rect.top) / rect.height),
      );
      setChromeStyle((prev) => ({
        ...prev,
        [which]: { ...prev[which], x, y },
      }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const titleBarPos =
    chromeStyle.title.x != null && chromeStyle.title.y != null
      ? {
          left: `${chromeStyle.title.x * 100}%`,
          top: `${chromeStyle.title.y * 100}%`,
          right: "auto",
          bottom: "auto",
        }
      : { top: 0, right: 0 };
  const studioBarPos =
    chromeStyle.studio.x != null && chromeStyle.studio.y != null
      ? {
          left: `${chromeStyle.studio.x * 100}%`,
          top: `${chromeStyle.studio.y * 100}%`,
          right: "auto",
          bottom: "auto",
        }
      : { bottom: 0, left: 0 };

  return (
    <Stack
      spacing={0}
      sx={{
        flex: 1,
        height: "100%",
        minHeight: 0,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Backdrop
        open={exporting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: alpha("#0f172a", 0.55),
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#fff" }} />
        <Typography fontWeight={800} sx={{ color: "#fff", fontSize: 18 }}>
          Exportando PDF…
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
          Guardando páginas y generando el archivo
        </Typography>
      </Backdrop>

      {peerViewers.length || staleConflict ? (
        <Alert
          severity={staleConflict ? "warning" : "info"}
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            py: 0.25,
            "& .MuiAlert-message": { width: "100%" },
          }}
          action={
            staleConflict ? (
              <Button
                color="inherit"
                size="small"
                sx={{ fontWeight: 800, textTransform: "none" }}
                onClick={() => handleStaleConflict()}
              >
                Recargar
              </Button>
            ) : null
          }
        >
          {staleConflict ? "Otro usuario guardó una versión más nueva." : null}
          {peerViewers.length
            ? `${staleConflict ? " · " : ""}${peerViewers
                .map((v) => v.username || "Alguien")
                .join(", ")} también ${
                peerViewers.length === 1 ? "está" : "están"
              } en este board.`
            : null}
        </Alert>
      ) : null}

      {/* Barras fijas: cabecera + páginas/casillas (fuera del scroll) */}
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 40,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 6px 16px rgba(15,23,42,0.06)",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={800} noWrap>
                {titulo || board.Titulo}
              </Typography>
              <Chip
                size="small"
                label={moodboardStatusLabel(board.Status)}
                color={board.Status === "ready" ? "success" : "default"}
                sx={{ height: 22, fontWeight: 700, flexShrink: 0 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[board.CustomerName, board.Temporada]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexShrink={0}
            flexWrap="wrap"
            useFlexGap
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.15,
                px: 0.5,
                py: 0.15,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  px: 0.6,
                  fontWeight: 800,
                  fontSize: "0.62rem",
                  letterSpacing: 0.04,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  userSelect: "none",
                }}
              >
                Board
              </Typography>
              {typeof onOpenHelp === "function" ? (
                <Tooltip title="Instrucciones">
                  <IconButton
                    size="small"
                    onClick={onOpenHelp}
                    aria-label="Instrucciones"
                  >
                    <HelpOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title={exporting ? "Exportando…" : "Exportar PDF"}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={busy || exporting || !assets.length}
                    onClick={handleExport}
                    aria-label="Exportar PDF"
                  >
                    {exporting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PictureAsPdfOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {board.DrivePdfFileUrl ? (
                <Tooltip title="Abrir PDF en Drive">
                  <IconButton
                    size="small"
                    href={board.DrivePdfFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                  >
                    <OpenInNewOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Título y barras del PDF">
                <IconButton
                  size="small"
                  onClick={() => setMetaOpen(true)}
                  aria-label="Título y barras del PDF"
                >
                  <TextFieldsOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Más opciones">
                <IconButton
                  size="small"
                  onClick={(e) => setLayoutMenuEl(e.currentTarget)}
                  aria-label="Más opciones"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={layoutMenuEl}
                open={Boolean(layoutMenuEl)}
                onClose={() => setLayoutMenuEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  disabled={busy}
                  onClick={() => {
                    setLayoutMenuEl(null);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: "error.main" }}
                >
                  Eliminar moodboard
                </MenuItem>
              </Menu>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.15,
                px: 0.5,
                py: 0.15,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                border: "1px solid",
                borderColor: (t) => alpha(t.palette.primary.main, 0.18),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  px: 0.6,
                  fontWeight: 800,
                  fontSize: "0.62rem",
                  letterSpacing: 0.04,
                  color: "primary.main",
                  textTransform: "uppercase",
                  userSelect: "none",
                }}
              >
                Fotos
              </Typography>
              <Tooltip title="Añadir fotos del SEARCH">
                <span>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setPoolPick(new Set());
                      setAddPhotosOpen(true);
                    }}
                    aria-label="Añadir fotos"
                    sx={{ color: "warning.main" }}
                  >
                    <PhotoLibraryOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`Fotos del board (${assets.length})`}>
                <span>
                  <IconButton
                    size="small"
                    color={assets.length ? "primary" : "default"}
                    disabled={busy}
                    onClick={() => setBoardPhotosOpen(true)}
                    aria-label="Fotos del board"
                  >
                    <CollectionsOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Subir desde el PC">
                <span>
                  <IconButton
                    size="small"
                    component="label"
                    disabled={busy}
                    aria-label="Subir desde el PC"
                  >
                    <CloudUploadOutlinedIcon fontSize="small" />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        handleUpload(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Stack>
        </Stack>
        <Box sx={{ px: { xs: 1.5, md: 2 }, pb: 1, pt: 0.25 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.15,
                px: 0.5,
                py: 0.15,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
                border: "1px solid",
                borderColor: "divider",
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  px: 0.6,
                  fontWeight: 800,
                  fontSize: "0.62rem",
                  letterSpacing: 0.04,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  userSelect: "none",
                  flexShrink: 0,
                }}
              >
                Páginas
              </Typography>
              <Tabs
                value={pageIdx}
                onChange={(_, v) => {
                  setPageIdx(v);
                  setActiveSlotId(null);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 32,
                  flex: 1,
                  minWidth: 0,
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-flexContainer": { gap: 0.25 },
                  "& .MuiTabScrollButton-root": {
                    width: 28,
                    "&.Mui-disabled": { opacity: 0.25 },
                  },
                  "& .MuiTab-root": {
                    minHeight: 28,
                    minWidth: 0,
                    py: 0.25,
                    px: 1,
                    my: 0.25,
                    borderRadius: 1.25,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "text.secondary",
                    "&.Mui-selected": {
                      color: "primary.main",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    },
                    "&:hover": {
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
                    },
                  },
                }}
              >
                {pages.map((_, i) => (
                  <Tab key={i} label={`${i + 1}`} title={`Página ${i + 1}`} />
                ))}
              </Tabs>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ flexShrink: 0 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.15,
                  px: 0.5,
                  py: 0.15,
                  borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.6,
                    fontWeight: 800,
                    fontSize: "0.62rem",
                    letterSpacing: 0.04,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    userSelect: "none",
                  }}
                >
                  Página
                </Typography>
                <Tooltip title="Añadir página">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPages((p) => [
                        ...p.map(normalizeFreePage),
                        emptyFreePage(),
                      ]);
                      setPageIdx(pages.length);
                      setActiveSlotId(null);
                    }}
                  >
                    <AddOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    pages.length <= 1
                      ? "Tiene que quedar al menos una página"
                      : `Eliminar página ${pageIdx + 1}`
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={pages.length <= 1}
                      onClick={() => setPageRemoveOpen(true)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Plantilla Aviator (layout 1+6)">
                  <IconButton
                    size="small"
                    onClick={() => setAviatorConfirmOpen(true)}
                  >
                    <ViewQuiltOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reparte TODAS las fotos del moodboard en páginas Aviator (7 por página). Sustituye el layout actual.">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy || !assets.length}
                      onClick={() => setAutoPagesConfirmOpen(true)}
                    >
                      <GridViewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.15,
                  px: 0.5,
                  py: 0.15,
                  borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                  border: "1px solid",
                  borderColor: (t) => alpha(t.palette.primary.main, 0.18),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.6,
                    fontWeight: 800,
                    fontSize: "0.62rem",
                    letterSpacing: 0.04,
                    color: "primary.main",
                    textTransform: "uppercase",
                    userSelect: "none",
                  }}
                >
                  Casillas
                </Typography>
                <Tooltip title="Añadir casilla cuadrada">
                  <IconButton size="small" onClick={handleAddSlot}>
                    <CropFreeOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Añadir casilla circular">
                  <IconButton size="small" onClick={handleAddCircleSlot}>
                    <CircleOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    activeSlot?.shape === "circle"
                      ? "Pasar a cuadrada"
                      : "Pasar a circular"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={!activeSlotId}
                      onClick={handleToggleSlotShape}
                    >
                      {activeSlot?.shape === "circle" ? (
                        <CropSquareOutlinedIcon fontSize="small" />
                      ) : (
                        <CircleOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Traer al frente">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!activeSlotId}
                      onClick={handleBringToFront}
                    >
                      <FlipToFrontOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Enviar atrás">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!activeSlotId}
                      onClick={handleSendToBack}
                    >
                      <FlipToBackOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    activeAssetIsCutout
                      ? "Esta foto ya es un cutout — confirmar antes de generar otro"
                      : "Quitar fondo (PNG transparente)"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={busy || !activeSlot?.assetId}
                      onClick={handleRemoveBackground}
                    >
                      <AutoFixHighOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Borde al contorno (grosor y color)">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy || !activeSlot?.assetId}
                      onClick={(e) => setStrokeAnchor(e.currentTarget)}
                    >
                      <BorderColorOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Recortar foto">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy || !activeSlot?.assetId}
                      onClick={() => setCropOpen(true)}
                    >
                      <CropOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Stack>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            <Tooltip
              title={
                saveState === "error"
                  ? "Error al guardar — clic para reintentar"
                  : saveState === "unsaved"
                    ? "Hay cambios pendientes (se guardan solos)"
                    : saveState === "saving"
                      ? "Guardando…"
                      : "Todo guardado"
              }
            >
              <Button
                size="small"
                variant="text"
                disabled={busy || saveState === "saving"}
                onClick={handleSaveAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  flexShrink: 0,
                  minWidth: 0,
                  px: 1,
                  color:
                    saveState === "error"
                      ? "error.main"
                      : saveState === "unsaved"
                        ? "warning.dark"
                        : "text.secondary",
                }}
              >
                {saveState === "saving"
                  ? "Guardando…"
                  : saveState === "unsaved"
                    ? "Sin guardar…"
                    : saveState === "error"
                      ? "Reintentar"
                      : "Guardado"}
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: { xs: 1.25, md: 1.75 },
          pt: 1.25,
        }}
      >
        {error ? (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}
        {okMsg ? (
          <Alert
            severity="success"
            onClose={() => setOkMsg("")}
            sx={{ mb: 1.5 }}
          >
            {okMsg}
          </Alert>
        ) : null}

        <Box>
          {/* Canvas */}
          <Box>
            <Box
              sx={{
                position: "relative",
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: "10px",
                width: "100%",
                aspectRatio: "297 / 210",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
              }}
            >
              <Box
                ref={canvasRef}
                onClick={() => setActiveSlotId(null)}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  bgcolor: "#f3f3f3",
                }}
              >
                {layeredSlots.map((slot) => (
                  <FreeformSlotBox
                    key={slot.id}
                    slot={slot}
                    selected={activeSlotId === slot.id}
                    canvasRef={canvasRef}
                    canRemove={currentPage.slots.length > 1}
                    onSelect={setActiveSlotId}
                    onRequestRemove={(id) => setSlotRemoveId(id)}
                    onChange={(next) => {
                      updateCurrentPageSlots((slots) =>
                        slots.map((s) => (s.id === next.id ? next : s)),
                      );
                    }}
                  />
                ))}

                <Box
                  onPointerDown={(e) => startChromeBarDrag("title", e)}
                  title="Arrastra para mover la cabecera"
                  sx={{
                    position: "absolute",
                    ...titleBarPos,
                    bgcolor: chromeStyle.title.bg,
                    color: chromeStyle.title.text,
                    pl: `${1.9 * chromeStyle.title.barScale}rem`,
                    pr: `${1.75 * chromeStyle.title.barScale}rem`,
                    py: `${0.75 * chromeStyle.title.barScale}rem`,
                    zIndex: 8,
                    pointerEvents: "auto",
                    cursor: "grab",
                    userSelect: "none",
                    touchAction: "none",
                    maxWidth: `${Math.min(52, 38 * chromeStyle.title.barScale)}%`,
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    clipPath: `polygon(0 0, 100% 0, 100% 100%, ${1.85 * chromeStyle.title.barScale}rem 100%)`,
                    WebkitClipPath: `polygon(0 0, 100% 0, 100% 100%, ${1.85 * chromeStyle.title.barScale}rem 100%)`,
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 800,
                      fontSize: `${0.85 * chromeStyle.title.fontScale}rem`,
                      lineHeight: 1.2,
                      color: chromeStyle.title.text,
                    }}
                  >
                    {catParts.main || "Moodboard"}
                  </Typography>
                  {catParts.sub ? (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 400,
                        fontSize: `${0.8 * chromeStyle.title.fontScale}rem`,
                        lineHeight: 1.2,
                        color: chromeStyle.title.subText,
                      }}
                    >
                      - {catParts.sub}
                    </Typography>
                  ) : null}
                </Box>

                <Box
                  onPointerDown={(e) => startChromeBarDrag("studio", e)}
                  title="Arrastra para mover el pie"
                  sx={{
                    position: "absolute",
                    ...studioBarPos,
                    bgcolor: chromeStyle.studio.bg,
                    color: chromeStyle.studio.contact,
                    pl: `${1.5 * chromeStyle.studio.barScale}rem`,
                    pr: `${2.1 * chromeStyle.studio.barScale}rem`,
                    py: `${0.85 * chromeStyle.studio.barScale}rem`,
                    zIndex: 8,
                    pointerEvents: "auto",
                    cursor: "grab",
                    userSelect: "none",
                    touchAction: "none",
                    maxWidth: `${Math.min(55, 42 * chromeStyle.studio.barScale)}%`,
                    clipPath: `polygon(0 0, calc(100% - ${1.85 * chromeStyle.studio.barScale}rem) 0, 100% 100%, 0 100%)`,
                    WebkitClipPath: `polygon(0 0, calc(100% - ${1.85 * chromeStyle.studio.barScale}rem) 0, 100% 100%, 0 100%)`,
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: `${0.72 * chromeStyle.studio.fontScale}rem`,
                      lineHeight: 1.2,
                      color: chromeStyle.studio.name,
                    }}
                  >
                    {studioParts.studioName}
                  </Typography>
                  {studioParts.contactLine ? (
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: `${0.58 * chromeStyle.studio.fontScale}rem`,
                        lineHeight: 1.35,
                        color: chromeStyle.studio.contact,
                        mt: 0.25,
                      }}
                    >
                      {studioParts.contactLine}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75 }}
            >
              {activeSlotId
                ? "Arrastra = mover casilla · Alt+arrastrar = foto dentro · Alt+rueda = zoom · −/+ · ○/□ · capas · varita · borde · recorte. Cabecera/pie: arrastra la barra."
                : "Clic en una casilla. Arrastra cabecera o pie para moverlos. «Fotos del board» para colocar fotos."}
            </Typography>
          </Box>
        </Box>
      </Box>

      <MoodboardAddPhotosDialog
        open={addPhotosOpen}
        onClose={() => {
          if (!busy) {
            setAddPhotosOpen(false);
            setPoolPick(new Set());
          }
        }}
        busy={busy}
        pool={pool}
        poolLoading={poolLoading}
        poolError={poolError}
        usedSearchItemIds={usedSearchItemIds}
        pick={poolPick}
        setPick={setPoolPick}
        onConfirm={() => handleAddFromPool()}
        seasonLabel={board?.Temporada || ""}
        clientLabel={board?.CustomerName || board?.Titulo || ""}
      />

      <MoodboardBoardPhotosDialog
        open={boardPhotosOpen}
        onClose={() => {
          if (!busy) setBoardPhotosOpen(false);
        }}
        assets={assets}
        selectedIds={gallerySelected}
        usedOnPagesIds={usedOnPagesAssetIds}
        onToggleSelect={toggleGallerySelect}
        onClearSelect={() => setGallerySelected(new Set())}
        onPlaceOne={(id) => {
          if (activeSlotId) assignAssetToSlot(activeSlotId, id);
          else placeAssetInFirstEmpty(id);
        }}
        onPlaceSelected={() => {
          placeSelectedOnCurrentPage();
        }}
        onRemove={(id) => setAssetRemoveId(id)}
        pageLabel={`pág. ${pageIdx + 1}`}
        busy={busy}
      />

      <Dialog
        open={metaOpen}
        onClose={() => setMetaOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <TextFieldsOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              Título y barras del PDF
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Texto, tamaño y colores. En el lienzo puedes arrastrar cabecera y
              pie. Se guardan solos y salen en el PDF.
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label="Título"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Barra superior (cliente / temporada)"
              placeholder="Zara Global Urban · SS27"
              value={categoryLabel}
              onChange={(e) => setCategoryLabel(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Barra inferior (studio / contacto)"
              value={studioLine}
              onChange={(e) => setStudioLine(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />

            <Divider sx={{ my: 0.5 }} />

            <Typography variant="subtitle2" fontWeight={800}>
              Estilo barra título
            </Typography>
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Tamaño barra {Math.round(chromeStyle.title.barScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.title.barScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    title: { ...prev.title, barScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                Tamaño letra {Math.round(chromeStyle.title.fontScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.title.fontScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    title: { ...prev.title, fontScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Fondo
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.title.bg}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        title: { ...prev.title, bg: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Texto
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.title.text}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        title: { ...prev.title, text: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Subtítulo
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.title.subText}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        title: { ...prev.title, subText: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Typography variant="subtitle2" fontWeight={800} sx={{ pt: 0.5 }}>
              Estilo barra pie
            </Typography>
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Tamaño barra {Math.round(chromeStyle.studio.barScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.studio.barScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    studio: { ...prev.studio, barScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                Tamaño letra {Math.round(chromeStyle.studio.fontScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.studio.fontScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    studio: { ...prev.studio, fontScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Fondo
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.studio.bg}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        studio: { ...prev.studio, bg: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Nombre
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.studio.name}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        studio: { ...prev.studio, name: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Contacto
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.studio.contact}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        studio: { ...prev.studio, contact: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Button
              size="small"
              onClick={() => setChromeStyle(normalizeChromeStyle(null))}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Restaurar estilo por defecto
            </Button>
            <Button
              size="small"
              onClick={() =>
                setChromeStyle((prev) => ({
                  title: { ...prev.title, x: null, y: null },
                  studio: { ...prev.studio, x: null, y: null },
                }))
              }
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Posición cabecera/pie por defecto
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            variant="contained"
            onClick={() => setMetaOpen(false)}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Listo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(slotRemoveId)}
        onClose={() => setSlotRemoveId(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Quitar casilla</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se elimina esta casilla de la página. La foto sigue en el moodboard
            (abajo) si quieres volver a colocarla.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSlotRemoveId(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleRemoveSlot(slotRemoveId)}
          >
            Quitar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assetRemoveId)}
        onClose={() => !busy && setAssetRemoveId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2.5,
              flexShrink: 0,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#f3f3f3",
              boxShadow: `0 8px 20px ${alpha("#0f172a", 0.12)}`,
            }}
          >
            {assetRemoveId ? (
              <AssetThumb
                assetId={assetRemoveId}
                selected={false}
                transparentBg
                fit="contain"
              />
            ) : null}
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                  color: "error.main",
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                Quitar foto del board
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Se elimina de la galería y de cualquier casilla donde esté
              colocada. Esta acción no se puede deshacer desde aquí.
              {(() => {
                const used = pages.reduce(
                  (n, p) =>
                    n +
                    (p.slots || []).filter((s) => s.assetId === assetRemoveId)
                      .length,
                  0,
                );
                if (!used) return null;
                return (
                  <>
                    {" "}
                    Ahora mismo está en{" "}
                    <strong>
                      {used} casilla{used === 1 ? "" : "s"}
                    </strong>
                    .
                  </>
                );
              })()}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAssetRemoveId(null)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => handleRemoveAsset(assetRemoveId)}
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            Eliminar foto
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeBgConfirmOpen}
        onClose={() => !busy && setRemoveBgConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
              color: "warning.dark",
            }}
          >
            <AutoFixHighOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              Ya es un cutout
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              Esta foto ya tiene el fondo quitado. Si continúas se crea{" "}
              <strong>otro duplicado</strong> en la galería y se sustituye en
              las casillas. El cutout anterior se queda en el board.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setRemoveBgConfirmOpen(false)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={runRemoveBackground}
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoFixHighOutlinedIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            Crear otro cutout
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aviatorConfirmOpen}
        onClose={() => setAviatorConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 56,
              borderRadius: 2,
              flexShrink: 0,
              p: 0.6,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              border: "1px solid",
              borderColor: (t) => alpha(t.palette.primary.main, 0.22),
              display: "grid",
              gridTemplateColumns: "1.15fr 1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: 0.4,
            }}
            aria-hidden
          >
            <Box
              sx={{
                gridRow: "1 / 3",
                borderRadius: 0.75,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.45),
              }}
            />
            {Array.from({ length: 6 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  borderRadius: 0.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.28),
                }}
              />
            ))}
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.15, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <ViewQuiltOutlinedIcon color="primary" fontSize="small" />
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                Plantilla Aviator
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              La <strong>página {pageIdx + 1}</strong> se va a recolocar al
              layout Aviator (1 grande + 6 pequeñas). Las fotos de esta página{" "}
              <strong>no se borran</strong>; cambian tamaño y posición.
              {(() => {
                const n = (currentPage.slots || []).filter(
                  (s) => s.assetId,
                ).length;
                if (n <= 7) {
                  return n ? (
                    <>
                      {" "}
                      Ahora hay <strong>{n}</strong> foto{n === 1 ? "" : "s"} en
                      esta página.
                    </>
                  ) : null;
                }
                return (
                  <>
                    {" "}
                    Hay <strong>{n}</strong> fotos: solo las 7 primeras casillas
                    entran en la plantilla; el resto vuelve a la galería del
                    board (sin borrar).
                  </>
                );
              })()}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAviatorConfirmOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAviatorPreset}
            startIcon={<ViewQuiltOutlinedIcon />}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            Recolocar página
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoPagesConfirmOpen}
        onClose={() => !busy && setAutoPagesConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
              color: "warning.dark",
            }}
          >
            <GridViewOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              Repartir en páginas Aviator
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              Se van a <strong>rehacer todas las páginas</strong> del moodboard:
              las <strong>{assets.length}</strong> foto
              {assets.length === 1 ? "" : "s"} del board se colocan en layouts
              Aviator (7 por página).
              {pages.length > 1 ? (
                <>
                  {" "}
                  Ahora hay <strong>{pages.length}</strong> páginas; el layout
                  actual se sustituye.
                </>
              ) : null}{" "}
              Las fotos <strong>no se eliminan</strong> del board.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAutoPagesConfirmOpen(false)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy || !assets.length}
            onClick={handleAutoPages}
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <GridViewOutlinedIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            Repartir todo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pageRemoveOpen}
        onClose={() => setPageRemoveOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.error.main, 0.12),
              color: "error.main",
            }}
          >
            <DeleteOutlineIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              Eliminar página {pageIdx + 1}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              Se quita el layout de esta página del collage. Las fotos{" "}
              <strong>no se borran</strong>: siguen en la pestaña Moodboard para
              recolocarlas.
              {(currentPage.slots || []).filter((s) => s.assetId).length > 0 ? (
                <>
                  {" "}
                  Ahora mismo hay{" "}
                  <strong>
                    {(currentPage.slots || []).filter((s) => s.assetId).length}
                  </strong>{" "}
                  foto
                  {(currentPage.slots || []).filter((s) => s.assetId).length ===
                  1
                    ? ""
                    : "s"}{" "}
                  colocada
                  {(currentPage.slots || []).filter((s) => s.assetId).length ===
                  1
                    ? ""
                    : "s"}
                  .
                </>
              ) : null}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setPageRemoveOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRemovePage}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              boxShadow: "none",
            }}
          >
            Eliminar página
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(strokeAnchor)}
        anchorEl={strokeAnchor}
        onClose={() => setStrokeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, width: 280, borderRadius: 2 } }}
      >
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
          Borde al contorno
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Ideal tras quitar fondo. Grosor en píxeles de la imagen.
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Grosor: {strokeWidth}px
            </Typography>
            <Slider
              size="small"
              min={1}
              max={40}
              step={1}
              value={strokeWidth}
              onChange={(_, v) => setStrokeWidth(Number(v))}
            />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Color
            </Typography>
            <Box
              component="input"
              type="color"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              sx={{
                width: 36,
                height: 28,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 0,
                bgcolor: "transparent",
                cursor: "pointer",
              }}
            />
            <TextField
              size="small"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              sx={{ flex: 1, "& input": { fontSize: 12, py: 0.75 } }}
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            disabled={busy || !activeSlot?.assetId}
            onClick={handleApplyContourStroke}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Aplicar borde
          </Button>
        </Stack>
      </Popover>

      <MoodboardCropDialog
        open={cropOpen}
        assetId={activeSlot?.assetId || null}
        busy={busy}
        onClose={() => setCropOpen(false)}
        onConfirm={handleConfirmCrop}
      />

      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Eliminar moodboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se borra del CRM y el PDF/carpeta de Drive van a la papelera.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
